import { useEffect, useState } from "react";
import api, { fmtMXN, fmtDate } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { useAuth } from "@/context/AuthContext";
import ControlCenterTab from "@/components/direction/ControlCenterTab";
import { toast } from "sonner";
import {
  Gauge, TrendingUp, CheckCircle2, XCircle, Loader2, Receipt, Wallet,
  Building2, Wrench, Truck, Hammer, FileSignature, AlertTriangle,
} from "lucide-react";

const TABS = [
  { id: "indicadores", label: "Indicadores" },
  { id: "rentabilidad", label: "Rentabilidad por obra" },
  { id: "autorizaciones", label: "Autorizaciones" },
];

const Kpi = ({ label, value, color = "var(--cyan)", icon: Icon, sub }) => (
  <div className="panel p-4" data-testid={`kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
    <div className="flex items-center gap-2 mb-1">
      {Icon && <Icon className="w-4 h-4" style={{ color }} />}
      <span className="text-[10px] uppercase tracking-widest text-slate-500">{label}</span>
    </div>
    <div className="font-mono text-lg font-bold" style={{ color }}>{value}</div>
    {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
  </div>
);

const IndicadoresTab = () => {
  const now = new Date().getFullYear();
  const [year, setYear] = useState(now);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get("/direction/indicators", { params: year ? { year } : {} })
      .then(({ data }) => setData(data))
      .catch(() => toast.error("No se pudieron cargar los indicadores"))
      .finally(() => setLoading(false));
  }, [year]);
  if (loading) return <div className="text-center py-10"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-500" /></div>;
  if (!data) return null;
  const g = data.gastos || {};
  return (
    <div className="space-y-4" data-testid="indicadores-tab">
      <div className="flex items-center gap-2">
        <span className="text-[10px] uppercase tracking-widest text-slate-500">Ejercicio</span>
        <select data-testid="indicators-year" value={year || ""} onChange={(e) => setYear(e.target.value ? Number(e.target.value) : null)}
          className="input-cyber px-2 py-1 text-xs">
          <option value="">Histórico completo</option>
          {[now, now - 1, now - 2].map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">// Facturación de obras (Serie C)</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Kpi label="Facturado" value={fmtMXN(data.obras?.facturado)} icon={FileSignature} color="#22d3ee" />
          <Kpi label="Cobrado" value={fmtMXN(data.obras?.cobrado)} icon={TrendingUp} color="#86efac" />
          <Kpi label="Por cobrar" value={fmtMXN(data.obras?.por_cobrar)} icon={AlertTriangle} color="#f59e0b" />
        </div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">// Gastos</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Kpi label="Obra" value={fmtMXN(g.obra)} icon={Receipt} color="#f59e0b" sub={`incluye destajos ${fmtMXN(data.destajos_pagados)}`} />
          <Kpi label="Administrativos" value={fmtMXN(g.administrativos)} icon={Building2} color="#f59e0b" />
          <Kpi label="Taller" value={fmtMXN(g.taller)} icon={Wrench} color="#f59e0b" />
          <Kpi label="Flotilla" value={fmtMXN(g.flotilla)} icon={Truck} color="#f59e0b" />
          <Kpi label="Total gastos" value={fmtMXN(g.total)} icon={Receipt} color="#fca5a5" />
        </div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">// Nómina, ventas y compromisos</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label="Nómina pagada" value={fmtMXN(data.nomina_pagada)} icon={Wallet} color="#c4b5fd" />
          <Kpi label="Ventas facturadas" value={fmtMXN(data.ventas?.facturado)} icon={FileSignature} color="#22d3ee" sub={`cobrado ${fmtMXN(data.ventas?.cobrado)}`} />
          <Kpi label="Compromisos por pagar" value={fmtMXN(data.compromisos_por_pagar)} icon={AlertTriangle} color="#f59e0b" sub="gastos aprobados sin pagar" />
          <Kpi label="Pendientes de autorizar" value={data.pendientes_autorizacion} icon={Hammer} color={data.pendientes_autorizacion > 0 ? "#f59e0b" : "#86efac"} />
        </div>
      </div>
    </div>
  );
};

const RentabilidadTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/direction/profitability")
      .then(({ data }) => setData(data))
      .catch(() => toast.error("No se pudo cargar la rentabilidad"))
      .finally(() => setLoading(false));
  }, []);
  if (loading) return <div className="text-center py-10"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-500" /></div>;
  if (!data) return null;
  const t = data.totales || {};
  return (
    <div className="panel overflow-x-auto" data-testid="rentabilidad-tab">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b" style={{ borderColor: "var(--border)" }}>
            <th className="px-3 py-2">Obra</th>
            <th className="px-3 py-2 text-right">Avance</th>
            <th className="px-3 py-2 text-right">Contratado</th>
            <th className="px-3 py-2 text-right">Facturado</th>
            <th className="px-3 py-2 text-right">Cobrado</th>
            <th className="px-3 py-2 text-right">Por cobrar</th>
            <th className="px-3 py-2 text-right">Gastos</th>
            <th className="px-3 py-2 text-right">Resultado</th>
            <th className="px-3 py-2 text-right">Margen</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r) => (
            <tr key={r.project_id} className="border-b hover:bg-white/[0.02]" style={{ borderColor: "var(--border)" }} data-testid={`profit-row-${r.project_id}`}>
              <td className="px-3 py-2">
                <div className="text-slate-200 font-medium">{r.name}</div>
                <div className="text-[10px] text-slate-500 font-mono">{r.code} · {r.status}</div>
              </td>
              <td className="px-3 py-2 text-right font-mono text-slate-400">{Math.round(r.avance)}%</td>
              <td className="px-3 py-2 text-right font-mono text-slate-300">{fmtMXN(r.contratado)}</td>
              <td className="px-3 py-2 text-right font-mono" style={{ color: "#22d3ee" }}>{fmtMXN(r.facturado)}</td>
              <td className="px-3 py-2 text-right font-mono" style={{ color: "#86efac" }}>{fmtMXN(r.cobrado)}</td>
              <td className="px-3 py-2 text-right font-mono" style={{ color: r.por_cobrar > 0 ? "#f59e0b" : "var(--text-dim)" }}>{fmtMXN(r.por_cobrar)}</td>
              <td className="px-3 py-2 text-right font-mono" style={{ color: "#fca5a5" }}>{fmtMXN(r.gastos)}</td>
              <td className="px-3 py-2 text-right font-mono font-bold" style={{ color: r.resultado >= 0 ? "#86efac" : "#fca5a5" }}>{fmtMXN(r.resultado)}</td>
              <td className="px-3 py-2 text-right font-mono" style={{ color: r.margen_pct == null ? "var(--text-dim)" : r.margen_pct >= 0 ? "#86efac" : "#fca5a5" }}>
                {r.margen_pct == null ? "—" : `${r.margen_pct.toFixed(1)}%`}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="text-[11px] font-bold" style={{ background: "rgba(34,211,238,0.04)" }}>
            <td className="px-3 py-2 uppercase tracking-widest text-slate-400">Totales</td>
            <td />
            <td className="px-3 py-2 text-right font-mono text-slate-200">{fmtMXN(t.contratado)}</td>
            <td className="px-3 py-2 text-right font-mono" style={{ color: "#22d3ee" }}>{fmtMXN(t.facturado)}</td>
            <td className="px-3 py-2 text-right font-mono" style={{ color: "#86efac" }}>{fmtMXN(t.cobrado)}</td>
            <td className="px-3 py-2 text-right font-mono" style={{ color: "#f59e0b" }}>{fmtMXN(t.por_cobrar)}</td>
            <td className="px-3 py-2 text-right font-mono" style={{ color: "#fca5a5" }}>{fmtMXN(t.gastos)}</td>
            <td className="px-3 py-2 text-right font-mono" style={{ color: (t.resultado || 0) >= 0 ? "#86efac" : "#fca5a5" }}>{fmtMXN(t.resultado)}</td>
            <td />
          </tr>
        </tfoot>
      </table>
      {data.rows.length === 0 && <div className="p-8 text-center text-slate-500 text-sm">Sin obras registradas.</div>}
    </div>
  );
};

const AutorizacionesTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);
  const load = () => {
    api.get("/direction/approvals")
      .then(({ data }) => setData(data))
      .catch(() => toast.error("No se pudieron cargar las autorizaciones"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);
  const act = async (item, status) => {
    if (status === "rechazado" && !window.confirm(`¿Rechazar el gasto de ${fmtMXN(item.amount)}?`)) return;
    setActing(item.id);
    const url = item.type === "admin" ? `/admin-expenses/${item.id}` : `/expenses/${item.id}`;
    try {
      await api.patch(url, { status });
      toast.success(status === "aprobado" ? "Gasto aprobado" : "Gasto rechazado");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setActing(null); }
  };
  if (loading) return <div className="text-center py-10"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-500" /></div>;
  if (!data) return null;
  return (
    <div className="space-y-2" data-testid="autorizaciones-tab">
      <div className="text-xs text-slate-400">
        {data.items.length} pendiente{data.items.length === 1 ? "" : "s"} · <span className="font-mono" style={{ color: "#f59e0b" }}>{fmtMXN(data.total)}</span>
      </div>
      {data.items.length === 0 && (
        <div className="panel p-8 text-center text-slate-400 text-sm">
          <CheckCircle2 className="w-8 h-8 mx-auto mb-2" style={{ color: "#86efac" }} />
          No hay gastos pendientes de autorización.
        </div>
      )}
      {data.items.map((it) => (
        <div key={`${it.type}-${it.id}`} className="panel px-4 py-3 flex items-center gap-3 flex-wrap" data-testid={`approval-${it.id}`}>
          <span className="px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-widest"
            style={{ background: it.type === "admin" ? "rgba(196,181,253,0.12)" : "rgba(34,211,238,0.1)", color: it.type === "admin" ? "#c4b5fd" : "var(--cyan)" }}>
            {it.type === "admin" ? "Admin" : "Obra"}
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-sm text-slate-200 truncate">{it.description || it.category}</div>
            <div className="text-[10px] text-slate-500">{fmtDate(it.date)} · {it.context}{it.vendor ? ` · ${it.vendor}` : ""} · por {it.created_by_name || "—"}</div>
          </div>
          <div className="font-mono text-sm font-bold" style={{ color: "#f59e0b" }}>{fmtMXN(it.amount)}</div>
          <div className="flex items-center gap-1">
            <button data-testid={`approve-item-${it.id}`} onClick={() => act(it, "aprobado")} disabled={acting === it.id}
              className="btn-ghost px-2 py-1 text-[10px] uppercase tracking-widest flex items-center gap-1 disabled:opacity-50" style={{ color: "#86efac", border: "1px solid #16a34a" }}>
              <CheckCircle2 className="w-3.5 h-3.5" /> Aprobar
            </button>
            <button data-testid={`reject-item-${it.id}`} onClick={() => act(it, "rechazado")} disabled={acting === it.id}
              className="btn-ghost px-2 py-1 text-[10px] uppercase tracking-widest flex items-center gap-1 disabled:opacity-50" style={{ color: "#fca5a5", border: "1px solid #dc2626" }}>
              <XCircle className="w-3.5 h-3.5" /> Rechazar
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default function DirectionPanel() {
  const { user } = useAuth();
  const [tab, setTab] = useState("control");
  const isDir = ["admin", "contador"].includes(user?.role);
  const tabs = [{ id: "control", label: "Centro de Control" }, ...(isDir ? TABS : [])];
  return (
    <div data-testid="direction-panel">
      <PageHeader title="Panel Directivo" subtitle="Centro de control ejecutivo" testId="direction-header" />
      <div className="px-6 lg:px-8 py-6 space-y-5">
        <div className="flex gap-1 flex-wrap">
          {tabs.map((t) => (
            <button key={t.id} data-testid={`direction-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`px-3 py-1.5 text-[11px] uppercase tracking-widest rounded-sm border transition-all ${tab === t.id ? "text-cyan-300" : "text-slate-500 hover:text-slate-300"}`}
              style={{ borderColor: tab === t.id ? "var(--cyan)" : "var(--border)", background: tab === t.id ? "rgba(34,211,238,0.07)" : "transparent" }}>
              {t.label}
            </button>
          ))}
        </div>
        {tab === "control" && <ControlCenterTab user={user} />}
        {tab === "indicadores" && isDir && <IndicadoresTab />}
        {tab === "rentabilidad" && isDir && <RentabilidadTab />}
        {tab === "autorizaciones" && isDir && <AutorizacionesTab />}
      </div>
    </div>
  );
}
